import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Fieldset, Group, Stack } from '@mantine/core'

import { useSocCandidatos, useDefinirMembros, useSocTeams } from '@/hooks/useSoc'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'

const NIVEIS = [1, 2, 3] as const

const VAZIO: Record<string, number[]> = { '1': [], '2': [], '3': [] }

/**
 * `useDefinirMembros` (`useSoc.ts`) existia sem nenhuma tela chamar — a quarta
 * ocorrencia do padrao "peca existe, nada chama" nesta feature. Este editor
 * fecha isso: sem ele, o time de SOC nasce (Task 1/2) e continua sem NINGUEM
 * nos tres niveis, e o alarme que ja funciona nao tem a quem chegar.
 *
 * CRITICO (revisao, rodada 1): `definir_membros` e SUBSTITUICAO TOTAL — DELETE
 * de tudo, depois INSERT do que veio. Um editor que sempre nasce do estado
 * fixo `{'1':[],'2':[],'3':[]}` e deixa salvar apaga QUALQUER composicao
 * anterior, mesmo sem o operador tocar em nada. A selecao inicial por isso
 * vem de `nivel_atual` (candidatos.ts) — o nivel em que cada candidato JA
 * ESTA hoje — e nao de um default fixo. So nao se manifestou em producao
 * porque o unico caminho ate aqui era logo apos CRIAR o time (sem ninguem
 * para perder ainda); reabrir este editor num time ja povoado reintroduziria
 * o apagamento em silencio.
 */
export function EditorDeIntegrantes({ teamId }: { teamId: number }) {
  const candidatos = useSocCandidatos(teamId)
  const salvar = useDefinirMembros(teamId)
  const time = useSocTeams().data?.items.find((t) => t.id === teamId)
  const [porNivel, setPorNivel] = useState<Record<string, number[]>>(VAZIO)
  // Qual time `porNivel` reflete hoje. Sem isso, um refetch de `candidatos`
  // (foco de aba, invalidacao) sobrescreveria a selecao que o operador esta
  // editando na tela; e ao trocar de `teamId` a selecao do time anterior nao
  // pode vazar para o novo.
  const [carregadoDe, setCarregadoDe] = useState<number | null>(null)

  useEffect(() => {
    if (!candidatos.data || carregadoDe === teamId) return
    const inicial: Record<string, number[]> = { '1': [], '2': [], '3': [] }
    for (const u of candidatos.data.items) {
      if (u.nivel_atual != null) inicial[String(u.nivel_atual)].push(u.id)
    }
    setPorNivel(inicial)
    setCarregadoDe(teamId)
  }, [candidatos.data, teamId, carregadoDe])

  // Um membro tem NO MAXIMO um nivel por time — a PK (team_id, user_id) de
  // `notification_team_members` ja decide isso (MIGRATION_238), e faz
  // sentido no modelo: quem esta no N1 recebe primeiro, o N2 existe para
  // quando o N1 nao reconhece; a mesma pessoa nos dois seria paginada duas
  // vezes e o escalonamento nao escalaria para ninguem. Marcar num nivel
  // por isso RETIRA a pessoa de todos os outros — a tela nunca monta uma
  // selecao que a gravacao recusaria com 422 (o retorno visivel na propria
  // grade ja diz o que aconteceu: a pessoa some do nivel anterior).
  function alternar(nivel: number, userId: number) {
    setPorNivel((atual) => {
      const chave = String(nivel)
      const jaTem = atual[chave].includes(userId)
      const semEsteUsuario = Object.fromEntries(
        NIVEIS.map((n) => [String(n), atual[String(n)].filter((id) => id !== userId)]),
      )
      if (jaTem) return semEsteUsuario
      return { ...semEsteUsuario, [chave]: [...semEsteUsuario[chave], userId] }
    })
  }

  const itens = candidatos.data?.items ?? []
  const vazios = NIVEIS.filter((n) => porNivel[String(n)].length === 0)
  // Membro ATUAL do time que nao aparece em `itens` — perdeu `user_tenants`
  // no tenant ou foi desativado. `definir_membros` e substituicao total: sem
  // este aviso, essa pessoa some no proximo "Salvar integrantes" com o toast
  // verde "Integrantes salvos", sem uma palavra sobre quem foi removido.
  // Preserva-la nao e opcao (a gravacao recusaria por membership) — o unico
  // caminho que sobra e dizer ANTES de salvar.
  const inelegiveis = candidatos.data?.inelegiveis ?? []
  // Da organizacao do tenant e sem acesso a ele: sem isto a pessoa so nao
  // aparece, e quem acabou de cadastra-la conclui que o cadastro falhou
  // (TASK-11538). Fica sob `EstadoDeConsulta`: erro de leitura nao vira
  // "ninguem sem acesso".
  const semAcesso = candidatos.data?.sem_acesso ?? []

  // `isError` sem tratamento caia no MESMO `itens.length === 0` de "ninguem
  // tem acesso ao tenant" — falha de rede/403/500 virava a instrucao "conceda
  // acesso ao tenant antes de montar o time", que manda o operador fazer a
  // coisa ERRADA (o acesso pode ja existir; so a leitura falhou).
  // `EstadoDeConsulta` (mesmo padrao do resto do app) distingue os dois.
  return (
    <EstadoDeConsulta consulta={candidatos}>
      <Stack gap="md">
        {itens.length === 0 && (
          <Alert color="yellow">
            Ninguém tem acesso a este tenant ainda. Conceda acesso ao tenant antes
            de montar o time — sem isso a gravação recusa, e o alarme não teria a
            quem chegar.
          </Alert>
        )}

        {NIVEIS.map((nivel) => (
          <Fieldset key={nivel} legend={`Nível ${nivel}`} role="group" aria-label={`Nível ${nivel}`}>
            <Stack gap="xs">
              {itens.map((u) => (
                <Checkbox
                  key={u.id}
                  label={u.full_name || u.email}
                  checked={porNivel[String(nivel)].includes(u.id)}
                  onChange={() => alternar(nivel, u.id)}
                />
              ))}
            </Stack>
          </Fieldset>
        ))}

        {/* Nivel vazio NAO e detalhe: e o estado em que aquele nivel nao toca.
            Dizer por extenso, e nao so deixar a lista vazia falar por si. */}
        {vazios.length > 0 && (
          <Alert color="yellow">
            {vazios.map((n) => `Nível ${n} está sem ninguém`).join('. ')}. Alarme
            que escalar até esse nível não vai encontrar quem avisar.
          </Alert>
        )}

        {/* Preservar quem caiu daqui nao e possivel — a gravacao recusaria
            por membership. O aviso e o que sobra: dizer ANTES de salvar quem
            sai, nomeado, em vez de deixar sumir junto com o toast verde. */}
        {inelegiveis.length > 0 && (
          <Alert color="red">
            Ao salvar, {inelegiveis.length === 1 ? 'esta pessoa vai sair' : 'estas pessoas vão sair'}{' '}
            do time por terem perdido acesso ao tenant ou sido desativadas:{' '}
            {inelegiveis.map((u) => u.full_name || u.email).join(', ')}.
          </Alert>
        )}

        {semAcesso.length > 0 && (
          <Alert color="blue" title="Da organização, sem acesso a este tenant">
            Só pode ser integrante quem tem acesso ao tenant
            {time ? ` ${time.tenant_name}` : ''}. Peça à plataforma o acesso, ou ajuste o
            perfil da pessoa: {semAcesso.map((u) => u.full_name || u.email).join(', ')}.
          </Alert>
        )}

        <Group justify="flex-end">
          <Button
            onClick={() => salvar.mutate(porNivel)}
            loading={salvar.isPending}
            disabled={itens.length === 0}
          >
            Salvar integrantes
          </Button>
        </Group>
      </Stack>
    </EstadoDeConsulta>
  )
}
